import { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiLogin, apiLogout, apiRefreshToken, apiGetProjects, setSessionExpiredHandler } from './api';
import {
  setCredentials,
  setProject as setProjectAction,
  clearSession,
  selectToken,
  selectUser,
  selectProject as selectProjectState,
} from './store/authSlice';

function getTokenExp(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp ?? null;
  } catch {
    return null;
  }
}

const AuthContext  = createContext(null);
const CHANNEL_NAME = 'futurizer_auth';

export function AuthProvider({ children }) {
  const dispatch = useDispatch();
  const token    = useSelector(selectToken);
  const user     = useSelector(selectUser);
  const project  = useSelector(selectProjectState);

  // true while waiting for a cross-tab token response
  const [syncing, setSyncing] = useState(true);

  const channelRef = useRef(null);
  // refs so the message handler always sees the latest values
  const tokenRef = useRef(token);
  const userRef  = useRef(user);
  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { userRef.current  = user;  }, [user]);

  useEffect(() => {
    const ch = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = ch;

    ch.onmessage = ({ data }) => {
      switch (data.type) {
        case 'REQUEST_TOKEN':
          // Another tab is asking — share our token if we have one
          if (tokenRef.current) {
            ch.postMessage({ type: 'PROVIDE_TOKEN', token: tokenRef.current, user: userRef.current });
          }
          break;

        case 'PROVIDE_TOKEN':
          // An existing tab sent us their token
          if (!tokenRef.current && data.token) {
            dispatch(setCredentials({ token: data.token, user: data.user }));
          }
          setSyncing(false);
          break;

        case 'LOGOUT':
          // Another tab logged out — clear this tab too
          dispatch(clearSession());
          break;

        default:
          break;
      }
    };

    // Ask other tabs for a token; wait at most 300 ms then proceed
    ch.postMessage({ type: 'REQUEST_TOKEN' });
    const timer = setTimeout(() => setSyncing(false), 300);

    return () => {
      clearTimeout(timer);
      ch.close();
    };
  }, [dispatch]);

  // Register the 401-interceptor callback so api.js can update/clear the session
  useEffect(() => {
    setSessionExpiredHandler(({ type, token: newToken, user: newUser }) => {
      if (type === 'TOKEN_REFRESHED') {
        dispatch(setCredentials({ token: newToken, user: newUser }));
        channelRef.current?.postMessage({ type: 'PROVIDE_TOKEN', token: newToken, user: newUser });
      } else {
        dispatch(clearSession());
        channelRef.current?.postMessage({ type: 'LOGOUT' });
      }
    });
    return () => setSessionExpiredHandler(null);
  }, [dispatch]);

  // Auto-refresh: schedule a token refresh 60s before expiry
  useEffect(() => {
    if (!token) return;
    const exp = getTokenExp(token);
    if (!exp) return;
    const msUntilRefresh = (exp * 1000) - Date.now() - 60_000;
    if (msUntilRefresh <= 0) return;
    const timer = setTimeout(async () => {
      try {
        const { token: newToken, user: newUser } = await apiRefreshToken();
        dispatch(setCredentials({ token: newToken, user: newUser }));
        channelRef.current?.postMessage({ type: 'PROVIDE_TOKEN', token: newToken, user: newUser });
      } catch {
        dispatch(clearSession());
        channelRef.current?.postMessage({ type: 'LOGOUT' });
      }
    }, msUntilRefresh);
    return () => clearTimeout(timer);
  }, [token, dispatch]);

  const login = useCallback(async (identifier, password) => {
    const { token: t, user: u } = await apiLogin(identifier, password);
    dispatch(setCredentials({ token: t, user: u }));
    channelRef.current?.postMessage({ type: 'PROVIDE_TOKEN', token: t, user: u });
  }, [dispatch]);

  const selectProject = useCallback((proj) => {
    dispatch(setProjectAction(proj));
  }, [dispatch]);

  const logout = useCallback(() => {
    const currentToken = tokenRef.current;
    dispatch(clearSession());
    channelRef.current?.postMessage({ type: 'LOGOUT' });
    if (currentToken) apiLogout(currentToken).catch(() => {});
  }, [dispatch]);

  return (
    <AuthContext.Provider value={{ token, user, project, syncing, login, selectProject, logout, apiGetProjects }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
